import styled from '@emotion/styled';

/**
 * Shared keyboard-focus treatments.
 *
 * These are Emotion CSS fragments meant to be interpolated into a styled component's
 * template literal, e.g. `styled('button')`...${focusRing}`;`.
 *
 * One constraint shapes all of them: `src/app/shared/styles/global/global.scss` sets
 * `outline: 0px !important` on `input/button/div/form:focus`, and `:focus-visible` is a
 * subset of `:focus` — so an outline-based indicator can never render anywhere in this app.
 * Every indicator here is a `box-shadow` or a `background`.
 */

/** The house focus color. Also used for `:focus-within` borders on search inputs. */
export const FOCUS_BLUE = '#77A7FF';

/** The light-blue fill from the transposed tables' "Add property" trigger. */
export const FOCUS_FILL = '#E8F0FE';

/**
 * A plain blue border marking the keyboard-focused control. Preferred for buttons and
 * switches. `box-shadow` rather than a real `border` so it never shifts layout on controls
 * that have no border of their own.
 */
export const focusRing = `
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${FOCUS_BLUE};
  }
`;

/**
 * The same border, for a control whose focusable element is a child — a date-picker input,
 * say, which is `border: none` with its outline stripped and so has no default indicator.
 *
 * Gated on `[data-nav-mode='keyboard']` because neither `:focus-within` nor `:focus-visible`
 * can tell mouse from keyboard on a text input: `:focus-within` matches any focus, and per
 * spec `:focus-visible` matches an input even on a mouse click, since typing is expected
 * there. The consuming modal must mirror the mode onto an ancestor — see
 * `shared/utils/keyboardNavMode`.
 *
 * MUI checkboxes deliberately should not use this; they already have an indicator.
 */
export const focusWithinRing = `
  border-radius: 4px;
  [data-nav-mode='keyboard'] &:focus-within {
    box-shadow: 0 0 0 2px ${FOCUS_BLUE};
  }
`;

/** A light blue fill behind the control rather than a ring around it. */
export const focusFill = `
  border-radius: 4px;
  &:focus { outline: none; }
  &:focus-visible {
    outline: none;
    background: ${FOCUS_FILL};
  }
`;

/**
 * For a control with a large padded hit area — a header tab — where filling the whole button
 * reads as one big block. Fills an inner `<span>` instead, so the highlight hugs the text.
 * That span must be `TabLabel` below; the two are useless apart.
 */
export const focusFillLabel = `
  &:focus { outline: none; }
  &:focus-visible { outline: none; }
  &:focus-visible > span {
    background: ${FOCUS_FILL};
  }
`;

/**
 * The `> span` that `focusFillLabel` targets. The horizontal negative margin cancels the
 * padding so the label text does not shift; vertical padding on an inline element paints
 * outside the line box without affecting layout.
 */
export const TabLabel = styled('span')`
  border-radius: 4px;
  padding: 5px;
  margin: 0 -5px;
`;

/** Strips native button chrome so a control converted from a div renders identically. */
export const buttonReset = `
  appearance: none;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: inherit;
  padding: 0;
`;
